// Play Store version check — the data half of the force-upgrade gate.
//
// Port of Flutter's forceUpgrader.dart. Google publishes no version API, so the
// only way to learn the live version is to fetch the store LISTING PAGE and pull
// the number out of its markup — which is what the Dart does.
//
// Two Capacitor-specific notes:
//
//  • Must go through CapacitorHttp (NATIVE http). A WebView fetch() to
//    play.google.com is blocked by CORS and would fail every time. This is the
//    same reason OtaUpdater uses CapacitorHttp for its manifest.
//
//  • The version to compare is the NATIVE one from App.getInfo(), NOT
//    version.json. version.json travels with the OTA bundle, so after a live
//    update it reports a version Play never installed — comparing that would
//    tell a correctly-updated user to update again, or hide a needed upgrade.
//
// ⚠️ THE SCRAPE IS INHERENTLY FRAGILE. The regex reads undocumented markup that
// Google can change without notice. Every failure path here returns null and the
// gate treats null as "don't block" — a broken scrape must never lock users out
// of their own home. If forced upgrades silently stop happening, suspect this
// first and check the patterns below against the live page.

import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

const LOOKUP_TIMEOUT_MS = 5000;

export const isAndroid = () => {
  try {
    return Capacitor.getPlatform() === 'android';
  } catch (e) {
    return false;
  }
};

// "1.2.26" vs "1.2.25" -> true when `current` is behind `store`.
// Missing segments count as 0, so 1.3 vs 1.3.0 is NOT an upgrade.
export function isVersionLower(current, store) {
  const cur = String(current || '').split('.').map((n) => parseInt(n, 10) || 0);
  const sto = String(store || '').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(cur.length, sto.length);
  for (let i = 0; i < len; i += 1) {
    const c = cur[i] || 0;
    const s = sto[i] || 0;
    if (c < s) return true;
    if (c > s) return false;
  }
  return false;
}

// The installed native version + package name (what Play actually put on the
// device). Returns null off-native.
export async function getNativeInfo() {
  try {
    const info = await CapApp.getInfo();
    return { version: info.version, packageName: info.id };
  } catch (e) {
    return null;
  }
}

// Patterns tried in order against the store page.
// [0] is the Dart's — the version currently sits in a JSON-ish array literal.
// The rest are fallbacks from older/alternate markup, so a single layout change
// doesn't silently disable the gate.
const VERSION_PATTERNS = [
  /\[\[\["(\d+\.\d+(?:\.\d+)*)"\]\]/,
  /Current Version.{0,80}?(\d+\.\d+(?:\.\d+)*)/s,
  /"softwareVersion"\s*:\s*"(\d+\.\d+(?:\.\d+)*)"/,
];

export async function getPlayStoreVersion(packageName) {
  if (!packageName) return null;
  const url = `https://play.google.com/store/apps/details?id=${packageName}&hl=en`;
  try {
    const res = await CapacitorHttp.get({
      url,
      readTimeout: LOOKUP_TIMEOUT_MS,
      connectTimeout: LOOKUP_TIMEOUT_MS,
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (res.status !== 200 || typeof res.data !== 'string') return null;
    for (const re of VERSION_PATTERNS) {
      const m = res.data.match(re);
      if (m && m[1]) return m[1].trim();
    }
    // Page fetched but no version found — markup probably changed.
    // eslint-disable-next-line no-console
    console.warn('[ForceUpgrade] store page fetched but no version matched');
    return null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ForceUpgrade] store lookup failed:', e?.message || e);
    return null;
  }
}

// Whether a newer build is live on Play.
// Returns { blocked, current, store }. `blocked` is only ever true when BOTH
// versions were read successfully and the installed one is genuinely behind.
export async function checkStoreUpgrade() {
  if (!isAndroid()) return { blocked: false };
  const native = await getNativeInfo();
  if (!native || !native.version) return { blocked: false };
  const store = await getPlayStoreVersion(native.packageName);
  if (!store) return { blocked: false, current: native.version };
  return {
    blocked: isVersionLower(native.version, store),
    current: native.version,
    store,
  };
}

// Open the listing. market:// hands straight to the Play app; the https URL is
// the fallback for a device without it.
export async function openPlayStore() {
  const native = await getNativeInfo();
  const pkg = native?.packageName || 'com.smartify.smartlifeindia';
  const web = `https://play.google.com/store/apps/details?id=${pkg}`;
  try {
    window.location.href = `market://details?id=${pkg}`;
    setTimeout(() => { window.open(web, '_blank'); }, 700);
  } catch (e) {
    window.open(web, '_blank');
  }
}
